/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/columns.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import type { PriceRoundingMode, Tier } from "../../lib/services/catalog";
import { formatDate } from "../../lib/format";
import { Badge, Flex, Text } from "rizzui";
import {
  PiListChecksDuotone as RulesIcon,
  PiEyeDuotone as EyeIcon,
  PiPencilSimpleDuotone as PencilIcon,
  PiTagDuotone as PricesIcon,
} from "react-icons/pi";
import { RowActions } from "../../components/table/row-actions";
import { StatusToggle } from "../../components/table/status-toggle";
import { tierPath } from "../../lib/paths";

const ROUNDING_LABEL: Record<PriceRoundingMode, string> = {
  none: "No rounding",
  rupee: "Nearest rupee",
  nearest_5: "Nearest 5",
};

/**
 * T059 — every field the task names, and nothing computed: `isDefault`, the two purchase-type
 * flags, `priceRoundingMode` and `effectiveFrom` all render exactly as the API returned them
 * (constitution I).
 */
export function tierColumns(setStatus?: (tier: Tier, active: boolean) => Promise<unknown>): ColumnDef<Tier>[] {
  return [
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
      size: 220,
      cell: ({ row }) => (
        <Flex direction="col" align="start" gap="1">
          <Link to={tierPath(row.original.slug)} className="font-medium text-gray-900 hover:underline">
            {row.original.name}
          </Link>
          {row.original.isDefault ? (
            <Badge variant="flat" color="primary" rounded="md">
              Default Tier
            </Badge>
          ) : null}
          {row.original.forNewCompanies ? (
            <Badge variant="flat" color="secondary" rounded="md">
              New companies
            </Badge>
          ) : null}
        </Flex>
      ),
    },
    {
      id: "purchaseTypes",
      header: "Purchase types",
      size: 200,
      cell: ({ row }) => (
        <Flex align="center" gap="2">
          {row.original.allowPiecePurchase ? (
            <Badge variant="flat" color="secondary" rounded="md">
              Piece
            </Badge>
          ) : null}
          {row.original.allowUnitPurchase ? (
            <Badge variant="flat" color="secondary" rounded="md">
              Unit
            </Badge>
          ) : null}
          {!row.original.allowPiecePurchase && !row.original.allowUnitPurchase ? <Text className="text-muted-foreground">None</Text> : null}
        </Flex>
      ),
    },
    {
      id: "priceRoundingMode",
      accessorKey: "priceRoundingMode",
      header: "Rounding mode",
      size: 160,
      cell: ({ row }) => ROUNDING_LABEL[row.original.priceRoundingMode],
    },
    {
      id: "effectiveFrom",
      // ISO date string — sorts chronologically as text.
      accessorKey: "effectiveFrom",
      header: "Effective from",
      size: 150,
      cell: ({ row }) => formatDate(row.original.effectiveFrom),
    },
    {
      id: "status",
      accessorKey: "isActive",
      header: "Status",
      size: 130,
      cell: ({ row }) => (
        <StatusToggle name={row.original.name} active={row.original.isActive} onChange={setStatus && ((next) => setStatus(row.original, next))} />
      ),
    },
    // The detail screen is the tier's edit form (a pencil; an eye for a read-only role, who gets it read-only); rules and generated prices
    // are the tier's two other screens. No delete: no endpoint removes a tier.
    {
      id: "actions",
      header: "",
      size: 110,
      cell: ({ row }) => (
        <RowActions
          name={row.original.name}
          primary={{ label: setStatus ? "Edit tier" : "View tier", icon: setStatus ? PencilIcon : EyeIcon, to: tierPath(row.original.slug) }}
          more={[
            { label: "Discount rules", icon: RulesIcon, to: `${tierPath(row.original.slug)}/rules` },
            { label: "Generated prices", icon: PricesIcon, to: `${tierPath(row.original.slug)}/prices` },
          ]}
        />
      ),
    },
  ];
}
